<template>
  <ion-page>
    <PageHeaderWithMenu title="Загрузка медиатеки" :scrolled="headerScrolled" />
    <ion-content :class="[isMobile ? 'main-background' : 'liga-web-background']" :fullscreen="true" @ionScroll="onContentScroll">
      <div class="media-layout">
        <aside class="media-nav">
          <div class="media-tree-header">
            <h3 class="media-nav-heading">Каталог медиатеки</h3>
            <ion-button fill="clear" size="small" class="tree-header-add" @click="openAddSectionModal()" title="Добавить подраздел">
              <ion-icon slot="icon-only" :icon="addOutline"></ion-icon>
            </ion-button>
          </div>

          <div class="media-tree">
            <div v-for="category in mediaCategories" :key="category.key" class="tree-folder">
              <div
                class="tree-folder-row"
                :class="{ 'tree-folder-row--open': isCategoryExpanded(category.key), 'tree-folder-row--active': selectedMediaCategory === category.key }"
              >
                <button type="button" class="tree-folder-main" @click="toggleCategory(category.key)">
                  <ion-icon class="tree-chevron" :icon="isCategoryExpanded(category.key) ? chevronDownOutline : chevronForwardOutline" />
                  <ion-icon class="tree-folder-icon" :icon="folderOutline" />
                  <span class="tree-folder-label">{{ category.label }}</span>
                  <span class="tree-folder-count">{{ sectionsFor(category.key).length }}</span>
                </button>
                <ion-button
                  fill="clear"
                  size="small"
                  class="tree-row-action"
                  title="Добавить подраздел"
                  @click.stop="openAddSectionModal(category.key)"
                >
                  <ion-icon slot="icon-only" :icon="addOutline"></ion-icon>
                </ion-button>
              </div>

              <div v-show="isCategoryExpanded(category.key)" class="tree-children">
                <p v-if="!sectionsFor(category.key).length" class="tree-empty">Нет подразделов</p>
                <div
                  v-for="s in sectionsFor(category.key)"
                  :key="s.id || s.folderKey"
                  class="tree-leaf-row"
                  :class="{ 'tree-leaf-row--active': selectedSectionKey === s.folderKey && selectedMediaCategory === category.key }"
                >
                  <button type="button" class="tree-leaf-main" @click="selectSection(category.key, s.folderKey)">
                    <ion-icon class="tree-leaf-icon" :icon="documentOutline" />
                    <span class="tree-leaf-text">
                      <span class="tree-leaf-name">{{ s.name }}</span>
                      <span class="tree-leaf-meta">
                        {{ s.folderKey }}
                        ·
                        {{ s.availableToAll ? 'всем' : roleLabel(s.roleName) }}
                      </span>
                    </span>
                  </button>
                  <ion-button
                    fill="clear"
                    size="small"
                    class="tree-row-action"
                    title="Доступы и название"
                    @click.stop="openEditSectionModal(s, category.key)"
                  >
                    <ion-icon slot="icon-only" :icon="keyOutline"></ion-icon>
                  </ion-button>
                </div>
              </div>
            </div>
          </div>
        </aside>

        <div class="media-main">
          <div class="disk-bar" :class="diskUsageClass">
            <div class="disk-bar-fill" :style="{ width: diskUsagePercent + '%' }"></div>
            <div class="disk-bar-content">
              <span class="disk-bar-label">Место на сервере</span>
              <span v-if="diskSpace" class="disk-bar-value">
                Занято {{ diskUsagePercent }}% · свободно {{ formatBytes(diskSpace.availableBytes) }} из {{ formatBytes(diskSpace.totalBytes) }}
              </span>
              <span v-else class="disk-bar-value">Загрузка…</span>
            </div>
          </div>

          <div class="media-tab-wrap">
            <ion-segment :value="mediaTab" @ionChange="onMediaTabChange($event)" mode="ios" class="liga-glass-segment media-tab-segment">
              <ion-segment-button mode="ios" value="video">
                <ion-label>Видео</ion-label>
              </ion-segment-button>
              <ion-segment-button mode="ios" value="pdf">
                <ion-label>PDF</ion-label>
              </ion-segment-button>
            </ion-segment>
          </div>

          <div v-show="mediaTab === 'video'" class="media-tab-panel">
            <AdminVideoUpload
              embed-mode
              :media-category="selectedMediaCategory"
              :section-folder-key="selectedSectionKey"
            />
          </div>

          <div v-show="mediaTab === 'pdf'" class="media-tab-panel">
            <p v-if="!selectedSectionKey" class="empty-hint embed-pick-hint">Выберите подраздел выше, чтобы загрузить или просмотреть PDF.</p>
            <template v-else>
              <ion-card class="admin-card">
                <ion-card-header>
                  <ion-card-title>Загрузить PDF в «{{ selectedSectionTitle }}»</ion-card-title>
                </ion-card-header>
                <ion-card-content>
                  <ion-item>
                    <ion-input label="Название для пользователей" label-placement="stacked" v-model="pdfUploadDisplayName"
                      placeholder="Отображаемое название материала"></ion-input>
                  </ion-item>
                  <input type="file" ref="pdfFileInput" accept=".pdf,application/pdf" class="file-input-hidden" @change="onPdfFileSelected" />
                  <div
                    class="drop-zone"
                    :class="{ 'drop-zone--active': pdfDragOver, 'drop-zone--has-file': !!selectedPdfFile, 'drop-zone--disabled': pdfUploadInProgress }"
                    @dragenter.prevent="onPdfDragEnter"
                    @dragover.prevent="onPdfDragOver"
                    @dragleave.prevent="onPdfDragLeave"
                    @drop.prevent="onPdfDrop"
                    @click="pickPdfFile()"
                  >
                    <ion-icon class="drop-zone-icon" :icon="cloudUploadOutline"></ion-icon>
                    <p class="drop-zone-title">{{ selectedPdfFile ? selectedPdfFile.name : 'Перетащите PDF сюда' }}</p>
                    <p class="drop-zone-hint">
                      <template v-if="selectedPdfFile">{{ formatBytes(selectedPdfFile.size) }} · нажмите, чтобы заменить</template>
                      <template v-else>или нажмите, чтобы выбрать файл</template>
                    </p>
                  </div>
                  <ion-button expand="block" @click="doPdfUpload()" :disabled="!canPdfUpload || pdfUploadInProgress">
                    {{ pdfUploadInProgress ? 'Загрузка…' : 'Загрузить PDF' }}
                  </ion-button>
                </ion-card-content>
              </ion-card>

              <ion-card class="admin-card">
                <ion-card-header>
                  <ion-card-title>PDF «{{ selectedSectionTitle }}»</ion-card-title>
                  <p class="admin-card-download-hint">Переключатель «Скачивание» разрешает или запрещает пользователям скачивать файл к себе.</p>
                </ion-card-header>
                <ion-card-content>
                  <div v-if="pdfs.length" class="media-file-list">
                    <div v-for="p in pdfs" :key="p.id" class="media-file-row">
                      <div class="media-file-main">
                        <div class="media-file-title">{{ p.displayName }}</div>
                        <div class="media-file-meta">{{ formatBytes(p.fileSizeBytes || 0) }}</div>
                      </div>
                      <label class="media-file-toggle" title="Разрешить скачивание пользователям">
                        <ion-toggle
                          :checked="!!p.allowDownload"
                          @ionChange="onPdfAllowDownloadChange(p, $event)"
                          aria-label="Скачивание"
                        ></ion-toggle>
                        <span>Скачивание</span>
                      </label>
                      <div class="media-file-actions">
                        <button type="button" class="media-icon-btn" @click="renamePdf(p)" title="Переименовать">
                          <ion-icon :icon="pencilOutline"></ion-icon>
                        </button>
                        <button type="button" class="media-icon-btn media-icon-btn--danger" @click="deletePdf(p)" title="Удалить">
                          <ion-icon :icon="trashOutline"></ion-icon>
                        </button>
                      </div>
                    </div>
                  </div>
                  <p v-else class="empty-hint">Нет PDF материалов в этом подразделе.</p>
                </ion-card-content>
              </ion-card>
            </template>
          </div>
        </div>
      </div>
    </ion-content>

    <!-- Добавить подраздел -->
    <ion-modal :is-open="showAddModal" @didDismiss="closeAddSectionModal()" class="admin-releases-modal media-section-modal">
      <ion-header>
        <ion-toolbar>
          <ion-title>Новый подраздел</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="closeAddSectionModal()">
              <ion-icon :icon="closeOutline"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding">
        <ion-item>
          <ion-select label="Главный раздел" label-placement="stacked" v-model="addForm.mediaCategory"
            interface="popover" mode="ios">
            <ion-select-option v-for="c in mediaCategories" :key="c.key" :value="c.key">{{ c.label }}</ion-select-option>
          </ion-select>
        </ion-item>
        <ion-item>
          <ion-input label="Название (для пользователей)" label-placement="stacked" v-model="addForm.name"
            placeholder="Например: Академия"></ion-input>
        </ion-item>
        <ion-item>
          <ion-input label="Ключ папки (латиница)" label-placement="stacked" v-model="addForm.folderKey"
            placeholder="Academia, CoachGP"></ion-input>
        </ion-item>
        <label class="section-toggle">
          <span>Доступно всем</span>
          <ion-toggle v-model="addForm.availableToAll" mode="ios"></ion-toggle>
        </label>
        <ion-item :class="{ 'section-role-disabled': addForm.availableToAll }">
          <ion-select label="Роль доступа" label-placement="stacked" v-model="addForm.roleName"
            interface="popover" mode="ios" placeholder="Выберите роль" :disabled="addForm.availableToAll">
            <ion-select-option v-for="a in allAuthorities" :key="a.name" :value="a.name">{{ a.description || a.name }}</ion-select-option>
          </ion-select>
        </ion-item>
        <ion-button expand="block" class="ion-margin-top" @click="submitAddSection()" :disabled="!canSubmitAdd || addingSection">
          {{ addingSection ? 'Создание…' : 'Добавить подраздел' }}
        </ion-button>
      </ion-content>
    </ion-modal>

    <!-- Редактировать доступы -->
    <ion-modal :is-open="showEditModal" @didDismiss="closeEditSectionModal()" class="admin-releases-modal media-section-modal">
      <ion-header>
        <ion-toolbar>
          <ion-title>Доступы раздела</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="closeEditSectionModal()">
              <ion-icon :icon="closeOutline"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding" v-if="editForm">
        <p class="edit-folder-hint">Папка: {{ editForm.folderKey }} · {{ mediaCategoryLabel(editForm.mediaCategory) }}</p>
        <ion-item>
          <ion-input label="Название (для пользователей)" label-placement="stacked" v-model="editForm.name"></ion-input>
        </ion-item>
        <label class="section-toggle">
          <span>Доступно всем</span>
          <ion-toggle v-model="editForm.availableToAll" mode="ios"></ion-toggle>
        </label>
        <ion-item :class="{ 'section-role-disabled': editForm.availableToAll }">
          <ion-select label="Роль доступа" label-placement="stacked" v-model="editForm.roleName"
            interface="popover" mode="ios" placeholder="Выберите роль" :disabled="editForm.availableToAll">
            <ion-select-option v-for="a in allAuthorities" :key="a.name" :value="a.name">{{ a.description || a.name }}</ion-select-option>
          </ion-select>
        </ion-item>
        <ion-button expand="block" class="ion-margin-top" @click="submitEditSection()" :disabled="!canSubmitEdit || savingSection">
          {{ savingSection ? 'Сохранение…' : 'Сохранить' }}
        </ion-button>
        <ion-button expand="block" fill="outline" color="danger" class="ion-margin-top" @click="confirmDeleteSection()" :disabled="savingSection || !editForm.id">
          Удалить подраздел
        </ion-button>
      </ion-content>
    </ion-modal>
  </ion-page>
</template>

<script lang="ts">
import { Authority } from '@/model/authority.model';
import { EducationSection } from '@/model/education-section.model';
import { GlobalContext } from '@/model/globalcontext.model';
import { IEducationPdfMaterial } from '@/model/education-pdf-material.model';
import {
  DEFAULT_MEDIA_CATEGORY,
  MEDIA_CATEGORIES,
  MediaCategoryKey,
  mediaCategoryLabel,
  normalizeMediaCategory,
} from '@/model/media-category.model';
import { IDiskSpace } from '@/model/video-upload.model';
import AdminService from '@/services/admin.service';
import AdminVideoUpload from '@/views/admin/AdminVideoUpload.vue';
import {
  alertController,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToggle,
  IonToolbar,
  toastController,
} from '@ionic/vue';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import {
  addOutline,
  chevronDownOutline,
  chevronForwardOutline,
  closeOutline,
  cloudUploadOutline,
  documentOutline,
  folderOutline,
  keyOutline,
  pencilOutline,
  trashOutline,
} from 'ionicons/icons';
import { defineComponent, inject } from 'vue';

type SectionsByCategory = Record<MediaCategoryKey, EducationSection[]>;

function emptySectionsByCategory(): SectionsByCategory {
  return {
    TRAININGS: [],
    EXERCISES: [],
    WORKOUTS: [],
    EDUCATION: [],
  };
}

export default defineComponent({
  name: 'AdminMediaUpload',
  components: {
    AdminVideoUpload,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonModal,
    IonPage,
    IonSegment,
    IonSegmentButton,
    IonSelect,
    IonSelectOption,
    IonTitle,
    IonToggle,
    IonToolbar,
    PageHeaderWithMenu,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    return {
      isMobile: globalContext.isMobile,
      pencilOutline,
      trashOutline,
      addOutline,
      chevronDownOutline,
      chevronForwardOutline,
      closeOutline,
      cloudUploadOutline,
      documentOutline,
      folderOutline,
      keyOutline,
      mediaCategoryLabel,
    };
  },
  data() {
    return {
      adminService: new AdminService(),
      diskSpace: null as IDiskSpace | null,
      sectionsByCategory: emptySectionsByCategory(),
      expandedCategories: { [DEFAULT_MEDIA_CATEGORY]: true } as Record<string, boolean>,
      allAuthorities: [] as Authority[],
      mediaCategories: MEDIA_CATEGORIES,
      selectedMediaCategory: DEFAULT_MEDIA_CATEGORY as MediaCategoryKey,
      selectedSectionKey: '' as string,
      mediaTab: 'video' as 'video' | 'pdf',
      headerScrolled: false,
      pdfs: [] as IEducationPdfMaterial[],
      pdfUploadDisplayName: '',
      selectedPdfFile: null as File | null,
      pdfUploadInProgress: false,
      pdfDragOver: false,
      pdfDragDepth: 0,
      syncingQuery: false,
      showAddModal: false,
      showEditModal: false,
      addingSection: false,
      savingSection: false,
      addForm: {
        mediaCategory: DEFAULT_MEDIA_CATEGORY as MediaCategoryKey,
        name: '',
        folderKey: '',
        availableToAll: false,
        roleName: '' as string,
      },
      editForm: null as null | {
        id?: number;
        mediaCategory: MediaCategoryKey;
        name: string;
        folderKey: string;
        availableToAll: boolean;
        roleName: string;
        sortOrder?: number;
      },
    };
  },
  computed: {
    educationSections(): EducationSection[] {
      return this.sectionsFor(this.selectedMediaCategory);
    },
    selectedSectionTitle(): string {
      const found = this.educationSections.find((s) => s.folderKey === this.selectedSectionKey);
      return found?.name || this.selectedSectionKey;
    },
    diskUsagePercent(): number {
      if (!this.diskSpace?.totalBytes) return 0;
      const used = Math.max(0, this.diskSpace.totalBytes - this.diskSpace.availableBytes);
      return Math.min(100, Math.round((100 * used) / this.diskSpace.totalBytes));
    },
    diskUsageClass(): string {
      const p = this.diskUsagePercent;
      if (p >= 85) return 'disk-bar--critical';
      if (p >= 70) return 'disk-bar--warn';
      return 'disk-bar--ok';
    },
    canPdfUpload(): boolean {
      if (!this.selectedPdfFile || !this.selectedSectionKey || !this.pdfUploadDisplayName?.trim()) return false;
      if (!this.isPdfExtension(this.selectedPdfFile.name)) return false;
      return true;
    },
    canSubmitAdd(): boolean {
      return !!(
        this.addForm.name?.trim() &&
        this.addForm.folderKey?.trim() &&
        (this.addForm.availableToAll || this.addForm.roleName)
      );
    },
    canSubmitEdit(): boolean {
      if (!this.editForm?.name?.trim()) return false;
      return !!(this.editForm.availableToAll || this.editForm.roleName);
    },
  },
  watch: {
    '$route.query': {
      handler() {
        if (this.syncingQuery) return;
        this.applyQueryFromRoute();
      },
      deep: true,
    },
  },
  mounted() {
    this.applyQueryFromRoute();
    this.expandedCategories[this.selectedMediaCategory] = true;
    this.loadDiskSpace();
    void this.loadAuthorities();
    void this.loadAllSections(true);
  },
  methods: {
    onContentScroll(e: CustomEvent) {
      const scrollTop = e.detail?.scrollTop ?? 0;
      this.headerScrolled = scrollTop > 16;
    },
    sectionsFor(category: MediaCategoryKey): EducationSection[] {
      return this.sectionsByCategory[category] || [];
    },
    isCategoryExpanded(category: MediaCategoryKey): boolean {
      return !!this.expandedCategories[category];
    },
    toggleCategory(category: MediaCategoryKey) {
      this.expandedCategories = {
        ...this.expandedCategories,
        [category]: !this.expandedCategories[category],
      };
      if (this.expandedCategories[category] && this.selectedMediaCategory !== category) {
        this.selectedMediaCategory = category;
        this.selectedSectionKey = '';
        this.pushQuery();
        this.loadPdfs();
      }
    },
    roleLabel(roleName?: string | null): string {
      if (!roleName) return 'роль не выбрана';
      const authority = this.allAuthorities.find((a) => a.name === roleName);
      return authority?.description || roleName;
    },
    applyQueryFromRoute() {
      const q = this.$route.query;
      const category = normalizeMediaCategory(typeof q.category === 'string' ? q.category : null);
      const section = typeof q.section === 'string' ? q.section : '';
      const tab = q.tab === 'pdf' ? 'pdf' : 'video';
      this.selectedMediaCategory = category;
      this.selectedSectionKey = section;
      this.mediaTab = tab;
      this.expandedCategories = { ...this.expandedCategories, [category]: true };
    },
    pushQuery() {
      this.syncingQuery = true;
      const query: Record<string, string> = {
        category: this.selectedMediaCategory,
        tab: this.mediaTab,
      };
      if (this.selectedSectionKey) {
        query.section = this.selectedSectionKey;
      }
      this.$router
        .replace({ name: 'AdminMediaUpload', query })
        .catch(() => undefined)
        .finally(() => {
          this.syncingQuery = false;
        });
    },
    selectSection(category: MediaCategoryKey, folderKey: string) {
      this.selectedMediaCategory = category;
      this.selectedSectionKey = folderKey;
      this.expandedCategories = { ...this.expandedCategories, [category]: true };
      this.pushQuery();
      this.loadPdfs();
    },
    onMediaTabChange(e: CustomEvent) {
      const value = e.detail?.value as string | undefined;
      if (value === 'video' || value === 'pdf') {
        this.mediaTab = value;
        this.pushQuery();
        if (value === 'pdf') {
          this.loadPdfs();
        }
      }
    },
    formatBytes(bytes: number): string {
      const n = Number(bytes);
      if (!Number.isFinite(n) || n < 0) return '—';
      if (n === 0) return '0 Б';
      const k = 1024;
      const sizes = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ', 'ПБ'];
      const i = Math.min(sizes.length - 1, Math.floor(Math.log(n) / Math.log(k)));
      return `${(n / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
    },
    isPdfExtension(fileName: string): boolean {
      const ext = (fileName || '').split('.').pop()?.toLowerCase();
      return ext === 'pdf';
    },
    async loadDiskSpace() {
      try {
        this.diskSpace = await this.adminService.getDiskSpace();
      } catch (e) {
        console.error('loadDiskSpace', e);
      }
    },
    async loadAuthorities() {
      try {
        this.allAuthorities = (await this.adminService.getAuthoritiesFull()).sort((a, b) =>
          (a.description || '').localeCompare(b.description || '')
        );
      } catch (e) {
        console.error('loadAuthorities', e);
      }
    },
    async loadAllSections(selectFirstIfNeeded = false) {
      try {
        const next = emptySectionsByCategory();
        await Promise.all(
          MEDIA_CATEGORIES.map(async (category) => {
            next[category.key] = await this.adminService.getEducationSections(category.key);
          })
        );
        this.sectionsByCategory = next;
        const current = this.sectionsFor(this.selectedMediaCategory);
        const keys = new Set(current.map((s) => s.folderKey));
        if (this.selectedSectionKey && !keys.has(this.selectedSectionKey)) {
          this.selectedSectionKey = '';
        }
        if (selectFirstIfNeeded && !this.selectedSectionKey && current.length) {
          this.selectedSectionKey = current[0].folderKey;
        }
        this.pushQuery();
        this.loadPdfs();
      } catch (e) {
        console.error('loadAllSections', e);
      }
    },
    openAddSectionModal(category?: MediaCategoryKey) {
      this.addForm = {
        mediaCategory: category || this.selectedMediaCategory,
        name: '',
        folderKey: '',
        availableToAll: false,
        roleName: '',
      };
      this.showAddModal = true;
    },
    closeAddSectionModal() {
      this.showAddModal = false;
    },
    async submitAddSection() {
      if (!this.canSubmitAdd) return;
      this.addingSection = true;
      try {
        await this.adminService.createEducationSection({
          name: this.addForm.name.trim(),
          folderKey: this.addForm.folderKey.trim(),
          availableToAll: this.addForm.availableToAll,
          roleName: this.addForm.availableToAll ? null : this.addForm.roleName,
          mediaCategory: this.addForm.mediaCategory,
        });
        const category = this.addForm.mediaCategory;
        const folderKey = this.addForm.folderKey.trim();
        const toast = await toastController.create({
          message: 'Подраздел добавлен',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.closeAddSectionModal();
        this.expandedCategories = { ...this.expandedCategories, [category]: true };
        await this.loadAllSections(false);
        this.selectSection(category, folderKey);
      } catch (e) {
        console.error('submitAddSection', e);
        const toast = await toastController.create({
          message: 'Ошибка добавления подраздела',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.addingSection = false;
      }
    },
    openEditSectionModal(section: EducationSection, category: MediaCategoryKey) {
      this.editForm = {
        id: section.id,
        mediaCategory: category,
        name: section.name,
        folderKey: section.folderKey,
        availableToAll: !!section.availableToAll,
        roleName: section.roleName || '',
        sortOrder: section.sortOrder,
      };
      this.showEditModal = true;
    },
    closeEditSectionModal() {
      this.showEditModal = false;
      this.editForm = null;
    },
    async submitEditSection() {
      if (!this.editForm?.id || !this.canSubmitEdit) return;
      this.savingSection = true;
      try {
        await this.adminService.updateEducationSection(this.editForm.id, {
          name: this.editForm.name.trim(),
          folderKey: this.editForm.folderKey,
          availableToAll: this.editForm.availableToAll,
          roleName: this.editForm.availableToAll ? null : this.editForm.roleName,
          mediaCategory: this.editForm.mediaCategory,
          sortOrder: this.editForm.sortOrder,
        });
        const toast = await toastController.create({
          message: 'Раздел сохранён',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.closeEditSectionModal();
        await this.loadAllSections(false);
      } catch (e) {
        console.error('submitEditSection', e);
        const toast = await toastController.create({
          message: 'Не удалось сохранить раздел',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.savingSection = false;
      }
    },
    async confirmDeleteSection() {
      if (!this.editForm?.id) return;
      const name = this.editForm.name;
      const id = this.editForm.id;
      const alert = await alertController.create({
        header: 'Удалить подраздел?',
        message: `Удалить «${name}»? Это действие нельзя отменить.`,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: () => {
              void this.deleteSection(id);
            },
          },
        ],
      });
      await alert.present();
    },
    async deleteSection(id: number) {
      try {
        await this.adminService.deleteEducationSection(id);
        const toast = await toastController.create({
          message: 'Подраздел удалён',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        if (this.editForm?.folderKey === this.selectedSectionKey) {
          this.selectedSectionKey = '';
        }
        this.closeEditSectionModal();
        await this.loadAllSections(false);
        this.pushQuery();
        this.loadPdfs();
      } catch (e) {
        console.error('deleteSection', e);
        const toast = await toastController.create({
          message: 'Ошибка удаления подраздела',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
    pickPdfFile() {
      if (this.pdfUploadInProgress) return;
      const input = this.$refs.pdfFileInput as HTMLInputElement | undefined;
      input?.click();
    },
    assignPdfFile(file: File | null) {
      if (!file) {
        this.selectedPdfFile = null;
        return;
      }
      if (!this.isPdfExtension(file.name) && file.type !== 'application/pdf') {
        this.selectedPdfFile = null;
        void toastController.create({
          message: 'Допустимы только файлы .pdf',
          duration: 2500,
          position: 'top',
          color: 'danger',
        }).then((t) => t.present());
        return;
      }
      this.selectedPdfFile = file;
    },
    onPdfFileSelected(ev: Event) {
      const input = ev.target as HTMLInputElement;
      this.assignPdfFile(input.files?.[0] || null);
      if (!this.selectedPdfFile && input) input.value = '';
    },
    onPdfDragEnter(ev: DragEvent) {
      if (this.pdfUploadInProgress) return;
      this.pdfDragDepth += 1;
      this.pdfDragOver = true;
      if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy';
    },
    onPdfDragOver(ev: DragEvent) {
      if (this.pdfUploadInProgress) return;
      this.pdfDragOver = true;
      if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy';
    },
    onPdfDragLeave() {
      this.pdfDragDepth = Math.max(0, this.pdfDragDepth - 1);
      if (this.pdfDragDepth === 0) this.pdfDragOver = false;
    },
    onPdfDrop(ev: DragEvent) {
      this.pdfDragDepth = 0;
      this.pdfDragOver = false;
      if (this.pdfUploadInProgress) return;
      const file = ev.dataTransfer?.files?.[0] || null;
      this.assignPdfFile(file);
    },
    async loadPdfs() {
      if (!this.selectedSectionKey) {
        this.pdfs = [];
        return;
      }
      try {
        this.pdfs = await this.adminService.getEducationPdfs(
          this.selectedSectionKey,
          this.selectedMediaCategory
        );
      } catch (e) {
        console.error('loadPdfs', e);
      }
    },
    async doPdfUpload() {
      if (!this.canPdfUpload || !this.selectedPdfFile) return;
      this.pdfUploadInProgress = true;
      try {
        await this.adminService.uploadPdf(
          this.selectedPdfFile,
          this.selectedSectionKey,
          this.pdfUploadDisplayName.trim(),
          this.selectedMediaCategory
        );
        const toast = await toastController.create({
          message: 'PDF загружен.',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.pdfUploadDisplayName = '';
        this.selectedPdfFile = null;
        const input = this.$refs.pdfFileInput as HTMLInputElement | undefined;
        if (input) input.value = '';
        this.loadDiskSpace();
        this.loadPdfs();
      } catch (e: unknown) {
        console.error('doPdfUpload', e);
        const err = e as Error & { response?: { status?: number } };
        const message = err.message?.includes('расширением') ? err.message
          : (err.response?.status === 400 ? 'Допустимы только файлы .pdf' : 'Ошибка загрузки PDF');
        const toast = await toastController.create({
          message,
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.pdfUploadInProgress = false;
      }
    },
    async onPdfAllowDownloadChange(pdf: IEducationPdfMaterial, event: CustomEvent) {
      const checked = !!event.detail?.checked;
      try {
        await this.adminService.updateEducationPdf(pdf.id, { allowDownload: checked });
        pdf.allowDownload = checked;
        const toast = await toastController.create({
          message: checked ? 'Скачивание разрешено' : 'Скачивание отключено',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error('onPdfAllowDownloadChange', e);
        const toast = await toastController.create({
          message: 'Ошибка сохранения',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
    async renamePdf(pdf: IEducationPdfMaterial) {
      const alert = await alertController.create({
        header: 'Переименовать материал',
        cssClass: 'alert-dark-theme',
        inputs: [{ name: 'displayName', type: 'text', value: pdf.displayName, placeholder: 'Новое название' }],
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Сохранить',
            handler: async (data: { displayName?: string }) => {
              const newName = (data.displayName || '').trim();
              if (!newName) return false;
              try {
                await this.adminService.updateEducationPdf(pdf.id, { displayName: newName });
                const toast = await toastController.create({
                  message: 'Название обновлено.',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
                this.loadPdfs();
              } catch (e) {
                console.error('renamePdf', e);
                const toast = await toastController.create({
                  message: 'Ошибка при переименовании.',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
              return true;
            },
          },
        ],
      });
      await alert.present();
    },
    async deletePdf(pdf: IEducationPdfMaterial) {
      const alert = await alertController.create({
        header: 'Удалить материал?',
        message: `Удалить «${pdf.displayName}»? Файл на сервере будет удалён.`,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: async () => {
              try {
                await this.adminService.deleteEducationPdf(pdf.id);
                const toast = await toastController.create({
                  message: 'PDF удалён.',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
                this.loadPdfs();
                this.loadDiskSpace();
              } catch (e) {
                console.error('deletePdf', e);
                const toast = await toastController.create({
                  message: 'Ошибка удаления.',
                  duration: 3000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
            },
          },
        ],
      });
      await alert.present();
    },
  },
});
</script>

<style scoped>
.media-layout {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding: 12px 12px 32px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.media-nav {
  margin: 0;
  padding: 16px 14px;
  border-radius: 16px;
  background: var(--liga-float-glass-bg-strong);
  border: 1px solid var(--liga-float-glass-border, rgba(255, 255, 255, 0.12));
  box-sizing: border-box;
}

.media-main {
  flex: 1;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.media-tree-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.media-nav-heading {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--liga-chrome-text-muted);
}

.tree-header-add {
  --color: var(--ion-color-orange);
  margin: 0;
}

.media-tree {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tree-folder {
  padding-bottom: 4px;
}

.tree-folder-row,
.tree-leaf-row {
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 12px;
}

.tree-folder-main,
.tree-leaf-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  border: none;
  background: transparent;
  color: var(--liga-chrome-text);
  text-align: left;
  padding: 11px 8px;
  border-radius: 12px;
  cursor: pointer;
}

.tree-folder-main:hover,
.tree-leaf-main:hover {
  background: rgba(255, 255, 255, 0.06);
}

.tree-folder-row--active > .tree-folder-main {
  background: rgba(232, 93, 4, 0.1);
}

.tree-leaf-row--active {
  background: rgba(232, 93, 4, 0.18);
}

.tree-leaf-row--active .tree-leaf-main {
  background: transparent;
}

.tree-chevron,
.tree-folder-icon,
.tree-leaf-icon {
  flex-shrink: 0;
  font-size: 1.15rem;
  color: var(--liga-chrome-text-muted);
}

.tree-folder-icon {
  color: var(--ion-color-orange);
}

.tree-folder-label {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  font-size: 1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tree-folder-count {
  flex-shrink: 0;
  font-size: 0.75rem;
  color: var(--liga-chrome-text-muted);
  background: rgba(127, 127, 127, 0.15);
  border-radius: 999px;
  padding: 2px 8px;
}

.tree-row-action {
  --color: var(--liga-chrome-text-muted);
  margin: 0;
  flex-shrink: 0;
}

.tree-children {
  padding: 4px 0 8px 22px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tree-empty {
  margin: 6px 0 10px;
  font-size: 0.85rem;
  color: var(--liga-chrome-text-muted);
}

.tree-leaf-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.tree-leaf-name {
  font-weight: 600;
  font-size: 0.92rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tree-leaf-meta {
  font-size: 0.75rem;
  color: var(--liga-chrome-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.embed-pick-hint {
  margin: 4px 0;
}

.disk-bar {
  position: relative;
  margin: 0;
  padding: 10px 14px;
  border-radius: 14px;
  background: var(--liga-float-glass-bg-strong);
  border: 1px solid var(--liga-float-glass-border, rgba(255, 255, 255, 0.12));
  overflow: hidden;
  isolation: isolate;
}

.disk-bar-fill {
  position: absolute;
  inset: 0 auto 0 0;
  z-index: 0;
  pointer-events: none;
  transition: width 0.35s ease, background-color 0.35s ease;
  border-radius: 14px 0 0 14px;
}

.disk-bar--ok .disk-bar-fill {
  background: rgba(46, 160, 67, 0.28);
}

.disk-bar--warn .disk-bar-fill {
  background: rgba(234, 179, 8, 0.32);
}

.disk-bar--critical .disk-bar-fill {
  background: rgba(239, 68, 68, 0.28);
}

.disk-bar-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.disk-bar-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--liga-chrome-text-muted);
}

.disk-bar-value {
  font-size: 0.95rem;
  color: var(--liga-chrome-text);
  word-break: break-word;
}

.admin-card {
  margin: 12px 0 0;
  --background: var(--liga-float-glass-bg-strong);
}

.admin-card ion-card-title {
  color: var(--liga-chrome-text);
  font-size: 1.05rem;
}

.admin-card-download-hint {
  color: var(--liga-chrome-text-muted);
  font-size: 0.85rem;
  margin-top: 4px;
  white-space: normal;
}

.admin-card ion-card-content {
  color: var(--liga-chrome-text);
}

.admin-card ion-item {
  --background: transparent !important;
  --background-activated: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
  --color: var(--liga-chrome-text);
}
.admin-card ion-item::part(native) {
  --background: transparent !important;
  background: transparent !important;
}

.media-tab-wrap {
  margin: 12px 0 0;
}

.media-tab-segment {
  margin: 0;
  width: 100%;
  max-width: 100%;
}

.media-tab-panel {
  margin-top: 0;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.file-info {
  margin: 8px 0;
  word-break: break-word;
}

.file-too-big {
  color: var(--ion-color-danger);
}

.empty-hint {
  color: var(--liga-chrome-text-muted);
}

.media-file-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.media-file-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  padding: 10px 4px;
  border-bottom: 1px solid var(--liga-float-glass-border, rgba(255, 255, 255, 0.1));
}

.media-file-row:last-child {
  border-bottom: none;
}

.media-file-main {
  flex: 1;
  min-width: 0;
}

.media-file-title {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--liga-chrome-text);
  word-break: break-word;
  line-height: 1.3;
}

.media-file-meta {
  margin-top: 2px;
  font-size: 0.75rem;
  color: var(--liga-chrome-text-muted);
}

.media-file-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  font-size: 0.78rem;
  color: var(--liga-chrome-text-muted);
  cursor: pointer;
}

.media-file-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.media-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--liga-chrome-text-muted);
  cursor: pointer;
  padding: 0;
}

.media-icon-btn:hover {
  background: rgba(127, 127, 127, 0.12);
  color: var(--liga-chrome-text);
}

.media-icon-btn--danger:hover {
  background: rgba(239, 68, 68, 0.12);
  color: var(--ion-color-danger);
}

.media-icon-btn ion-icon {
  font-size: 1.05rem;
}

.file-input-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.drop-zone {
  margin: 10px 0 4px;
  padding: 22px 16px;
  border: 1.5px dashed rgba(232, 93, 4, 0.45);
  border-radius: 14px;
  background: rgba(232, 93, 4, 0.04);
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  user-select: none;
}

.drop-zone:hover {
  border-color: var(--ion-color-orange);
  background: rgba(232, 93, 4, 0.08);
}

.drop-zone--active {
  border-color: var(--ion-color-orange);
  border-style: solid;
  background: rgba(232, 93, 4, 0.14);
}

.drop-zone--has-file {
  border-style: solid;
  border-color: rgba(232, 93, 4, 0.55);
}

.drop-zone--disabled {
  opacity: 0.55;
  pointer-events: none;
}

.drop-zone-icon {
  font-size: 1.75rem;
  color: var(--ion-color-orange);
  margin-bottom: 6px;
}

.drop-zone-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--liga-chrome-text);
  word-break: break-word;
}

.drop-zone-hint {
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: var(--liga-chrome-text-muted);
}

.admin-card ion-button:not([fill='clear']):not([fill='outline']) {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --color: var(--liga-on-accent);
  font-weight: bold;
  --border-radius: 14px;
  margin-top: 8px;
}

.admin-card ion-button[fill='outline'] {
  --border-color: var(--ion-color-orange);
  --color: var(--ion-color-orange);
  --border-radius: 14px;
  font-weight: 600;
  margin-top: 8px;
}

.admin-card ion-button[fill='clear'] {
  --color: var(--ion-color-orange);
  font-weight: 600;
}

/* Десктоп: две колонки только от 992px (как split-pane), не по isMobile */
@media (min-width: 992px) {
  .media-layout {
    flex-direction: row;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 8px 32px;
  }

  .media-nav {
    width: 300px;
    flex-shrink: 0;
    margin: 0;
    position: sticky;
    top: 88px;
    max-height: calc(100vh - 120px);
    overflow: auto;
  }

  .media-main {
    padding-right: 0;
  }

  .disk-bar-content {
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }

  .media-tab-wrap {
    margin: 12px 0 0;
  }

  .admin-card {
    margin: 12px 0 0;
  }
}
</style>

<!-- Стили модалок вне scoped: ion-modal телепортирует контент -->
<style>
.media-section-modal .section-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 12px 4px;
  color: var(--liga-chrome-text);
  font-size: 0.95rem;
}

.media-section-modal .section-role-disabled {
  opacity: 0.45;
  pointer-events: none;
}

.media-section-modal .edit-folder-hint {
  margin: 0 0 12px;
  font-size: 0.85rem;
  color: var(--liga-chrome-text-muted);
}
</style>
